import { FC, useState } from 'react';
import { IonButton, IonSpinner } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AlertPresenter } from '../../AlertPresenter';
import { Modal } from '../../components/Modal';
import { Text } from '../../components/Text';
import { QrCodeIcon } from '../../theme/Icons';
import { ITrackingFrom } from '../ITrackingFrom';
import { CARE_CODE_DESCRIPTION } from './careCodeCopy';
import { CareCodeService } from './CareCodeService';

const useStyles = createUseStyles({
    content: {
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 20px 20px 20px',
        height: '100%'
    },

    // Takes the space left over above the buttons and centres the icon and copy within it, so
    // the pair sits in the middle of the modal rather than hugging the title.
    body: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    },

    iconWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 16
    },

    iconCircle: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: '#EDF4F7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    description: {
        textAlign: 'center'
    },

    buttonRow: {
        display: 'flex',
        gap: 12,
        marginTop: 'auto',
        paddingTop: 24
    },

    button: {
        flex: 1
    }
});

export const CreateCareCodeModal: FC<{
    isOpen: boolean;
    patientFirstName: string;
    trackingFrom: ITrackingFrom;
    closeHandler: () => void;
}> = ({ isOpen, patientFirstName, trackingFrom, closeHandler }) => {
    const classes = useStyles();

    const careCodeService = useInstance(CareCodeService);
    const alert = useInstance(AlertPresenter);

    const [isCreating, setIsCreating] = useState(false);

    const createCareCode = async () => {
        setIsCreating(true);
        try {
            await careCodeService.createAndOpen({ from: trackingFrom });
            closeHandler();
        } catch (err: any) {
            alert.showAndLogError(err);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            closeHandler={closeHandler}
            title={`${patientFirstName}'s Care Code`}
            desktopMaxWidth={420}
            data-id="create-care-code-modal"
        >
            <div className={classes.content}>
                <div className={classes.body}>
                    <div className={classes.iconWrapper}>
                        <div className={classes.iconCircle}>
                            <QrCodeIcon color="primary" customSize={38} />
                        </div>
                    </div>

                    <div className={classes.description}>
                        <Text type="detail">{CARE_CODE_DESCRIPTION}</Text>
                    </div>
                </div>

                <div className={classes.buttonRow}>
                    <IonButton
                        fill="outline"
                        color="danger"
                        className={classes.button}
                        disabled={isCreating}
                        onClick={closeHandler}
                        data-id="cancel-care-code-btn"
                    >
                        Cancel
                    </IonButton>
                    <IonButton
                        className={classes.button}
                        disabled={isCreating}
                        onClick={createCareCode}
                        data-id="confirm-care-code-btn"
                    >
                        {isCreating ? <IonSpinner name="dots" /> : 'Create QR Code'}
                    </IonButton>
                </div>
            </div>
        </Modal>
    );
};
